/** API `code` when the session’s stored model is no longer allowed (admin / policy). */
export const CHAT_MODEL_DISABLED_CODE = 'chat_model_disabled' as const

/** User-facing copy when a chat’s stored model was turned off in admin settings (API errors). */
export const CHAT_MODEL_DISABLED_USER_MESSAGE =
  'That model isn’t available for new messages here. Pick another allowed model.'

/** Composer banner: brief context + action. */
export const SESSION_MODEL_BLOCKED_BANNER =
  'This chat uses a model that’s no longer enabled—often turned off in admin settings. Choose a model below to keep sending.'

/** Toast when user tries send/edit/regenerate while blocked. */
export const SESSION_MODEL_BLOCKED_TOAST =
  'This chat’s model isn’t enabled anymore. Choose another model to continue.'

/** Success after re-locking via dialog. */
export const SESSION_MODEL_RELOCK_SUCCESS_TOAST = 'Model updated—you can send again.'

/** Modal: title + body for the re-lock flow. */
export const SESSION_MODEL_RELOCK_TITLE = 'Choose a model'
export const SESSION_MODEL_RELOCK_DIALOG_BODY =
  'This conversation is tied to a model that’s no longer enabled. Pick one of the options below to unlock sending.'

export function chatApiMessageFromPayload(data: {
  code?: string
  error?: string
}): string | null {
  if (data.code === CHAT_MODEL_DISABLED_CODE) return CHAT_MODEL_DISABLED_USER_MESSAGE
  if (typeof data.error === 'string' && data.error.trim()) return data.error.trim()
  return null
}

/**
 * Read error JSON from a failed `Response` (consumes the body once).
 * Uses text + JSON.parse so we still parse when `Content-Type` is missing or wrong.
 */
export async function parseChatApiError(
  res: Response,
): Promise<{ code?: string; error?: string } | null> {
  try {
    const text = await res.text()
    const trimmed = text.trim()
    if (!trimmed) return null
    const data = JSON.parse(trimmed) as Record<string, unknown>
    if (!data || typeof data !== 'object') return null
    let code = typeof data.code === 'string' ? data.code : undefined
    let error: string | undefined
    if (typeof data.error === 'string') {
      error = data.error
    } else if (typeof data.detail === 'string') {
      error = data.detail
    } else if (Array.isArray(data.detail) && data.detail.length > 0) {
      const first = data.detail[0]
      if (typeof first === 'object' && first && 'msg' in first && typeof (first as { msg?: string }).msg === 'string') {
        error = (first as { msg: string }).msg
      }
    }
    return { code, error }
  } catch {
    return null
  }
}

/** True when the API indicates the chat’s stored model can’t be used (show re-lock UI). */
export function shouldOfferModelRelock(payload: { code?: string; error?: string } | null): boolean {
  if (!payload) return false
  if (payload.code === CHAT_MODEL_DISABLED_CODE) return true
  const e = typeof payload.error === 'string' ? payload.error.toLowerCase() : ''
  return e.includes('model for this chat') || (e.includes('no longer') && e.includes('model'))
}

export function chatErrorToastFromPayload(
  data: { code?: string; error?: string } | null,
  fallback: string,
): string {
  if (!data) return fallback
  return chatApiMessageFromPayload(data) ?? fallback
}

export async function getChatErrorToastMessage(res: Response, fallback: string): Promise<string> {
  const data = await parseChatApiError(res)
  return chatErrorToastFromPayload(data, fallback)
}
